"use client";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Clock, Mail, Phone } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion, Variants } from "framer-motion";
import { axiosInstance } from "@/lib/axiosInstance";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  firstName: z.string().min(2).max(255),
  lastName: z.string().min(2).max(255),
  email: z.string().email(),
  subject: z.string().min(2).max(255),
  message: z.string(),
});

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      subject: "General Inquiry",
      message: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      const payload = {
        user: {
          email: values.email,
          name: `${values.firstName ?? ""} ${values.lastName ?? ""}`,
        },
        inquiry: {
          subject: values.subject,
          message: values.message,
        },
      };
      await axiosInstance.post("/contact", payload);

      form.reset();
      toast.success("Message sent successfully", {
        description: "We will get back to you shortly.",
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("An error occurred while sending your message");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <motion.section
      id="contact"
      className={cn(
        "rounded-lg container py-24 sm:py-32 overflow-x-hidden bg-gradient-to-b from-muted/50 to-background",
      )}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <section className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-10 z-1">
        <motion.div variants={containerVariants}>
          <motion.div className="space-y-4 mb-8" variants={itemVariants}>
            <h2 className="text-lg font-semibold text-primary tracking-wider uppercase">
              Contact Us
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
              Get in Touch
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed lg:w-5/6">
              Have questions or need help? Our team is ready to support your
              business with our POS solution. Reach out anytime!
            </p>
          </motion.div>

          <div className="grid gap-6">
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="flex items-start space-x-4 bg-muted/40 p-4 rounded-lg hover:bg-muted/60 transition-colors"
            >
              <Phone className="h-6 w-6 text-primary" />
              <div>
                <div className="font-semibold mb-1">Call us</div>
                <a
                  href="tel:+254797313732"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  +254 797 313732
                </a>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="flex items-start space-x-4 bg-muted/40 p-4 rounded-lg hover:bg-muted/60 transition-colors"
            >
              <Mail className="h-6 w-6 text-primary" />
              <div>
                <div className="font-semibold mb-1">Email us</div>
                <a
                  href="mailto:carwash@mikrosell.com"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  carwash@mikrosell.com
                </a>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="flex items-start space-x-4 bg-muted/40 p-4 rounded-lg hover:bg-muted/60 transition-colors"
            >
              <Clock className="h-6 w-6 text-primary" />
              <div>
                <div className="font-semibold mb-1">Business hours</div>
                <div className="text-muted-foreground">
                  <div>Monday - Saturday</div>
                  <div>8AM - 6PM</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} initial="hidden" animate="visible">
          <Card className="shadow-lg border-muted bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <CardHeader>
              <motion.h3
                className="text-2xl font-semibold text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Send us a Message
              </motion.h3>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <motion.form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="grid w-full gap-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex flex-col md:flex-row gap-8">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="John"
                              {...field}
                              className="bg-muted/50 border-muted-foreground/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Doe"
                              {...field}
                              className="bg-muted/50 border-muted-foreground/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="john.doe@example.com"
                              {...field}
                              className="bg-muted/50 border-muted-foreground/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subject</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a subject" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="General Inquiry">
                                General Inquiry
                              </SelectItem>
                              <SelectItem value="Service Booking">
                                Service Booking
                              </SelectItem>
                              <SelectItem value="Feedback">Feedback</SelectItem>
                              <SelectItem value="Partnership">
                                Partnership
                              </SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={5}
                              placeholder="Your message..."
                              className="resize-none bg-muted/50 border-muted-foreground/20"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Button
                    disabled={isSubmitting}
                    className="w-full mt-6 bg-primary hover:bg-primary/90 transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="mr-2">Sending</span>
                        <span className="animate-pulse">...</span>
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                </motion.form>
              </Form>
            </CardContent>

            <CardFooter></CardFooter>
          </Card>
        </motion.div>
      </section>
    </motion.section>
  );
};
